// src/pages/DocViewerPage.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import FileViewerModal from "../components/FileViewerModal";

/**
 * DocViewerPage – immutable finalized Doc Writer artifact viewer.
 *
 * Canonical loading path:
 * - Doc History passes jobId, sessionId, revisionId, and publishedId.
 * - This page calls GET /docwriter/jobs/<jobId>/finalized-artifacts.
 * - The backend authenticates the user, verifies job ownership, and returns
 *   fresh signed URLs for the primary finalized PDF, DOCX, or TXT artifacts.
 * - The secondary File Vault copy is not used as the normal viewing source.
 *
 * FileViewerModal continues to own the viewing shell:
 * - PdfViewer renders PDF files and PDF controls.
 * - Microsoft Office Online renders Word files.
 * - TXT uses the FileViewerModal text path.
 *
 * This page is finalized-view only. It does not load the editor, save changes,
 * reconstruct a document from HTML, or call the old history/publish endpoints.
 */

const API_HOST = (
  process.env.REACT_APP_BACKEND_URL || ""
).replace(/\/+$/, "");

const DOCWRITER_JOBS_BASE =
  `${API_HOST}/docwriter/jobs`;

const FORMAT_ORDER = [
  "pdf",
  "docx",
  "text",
];

const FORMAT_DETAILS = {
  pdf: {
    label: "PDF",
    extension: "pdf",
    format: "pdf",
    contentType: "application/pdf",
  },
  docx: {
    label: "Word",
    extension: "docx",
    format: "office",
    contentType:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  },
  text: {
    label: "TXT",
    extension: "txt",
    format: "text",
    contentType:
      "text/plain;charset=utf-8",
  },
};

function safeString(value) {
  return String(value ?? "").trim();
}

function safeObject(value) {
  return value &&
    typeof value === "object" &&
    !Array.isArray(value)
    ? value
    : {};
}

function firstNonEmpty(...values) {
  for (const value of values) {
    if (
      value !== undefined &&
      value !== null &&
      safeString(value)
    ) {
      return value;
    }
  }

  return "";
}

function normalizeFormat(value) {
  const format =
    safeString(value).toLowerCase();

  if (
    format === "pdf" ||
    format === "application/pdf"
  ) {
    return "pdf";
  }

  if (
    [
      "doc",
      "docx",
      "word",
      "office",
      "application/msword",
    ].includes(format) ||
    /wordprocessingml/i.test(format)
  ) {
    return "docx";
  }

  if (
    [
      "txt",
      "text",
      "plain",
      "plaintext",
      "text/plain",
    ].includes(format)
  ) {
    return "text";
  }

  return "";
}

function ensureExtension(
  title,
  extension
) {
  const clean =
    safeString(title)
      .replace(/[\\/:*?"<>|]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 180) ||
    "Document";

  const expression =
    new RegExp(
      `\\.${extension}$`,
      "i"
    );

  return expression.test(clean)
    ? clean
    : `${clean}.${extension}`;
}

function isOpenableUrl(value) {
  const source =
    safeString(value);

  return (
    /^https?:\/\//i.test(source) ||
    source.startsWith("blob:") ||
    source.startsWith("data:")
  );
}

function absolutizeUrl(value) {
  const source =
    safeString(value);

  if (!source) {
    return "";
  }

  if (
    isOpenableUrl(source) ||
    source.startsWith("//")
  ) {
    return source.startsWith("//")
      ? `https:${source}`
      : source;
  }

  try {
    return new URL(
      source,
      API_HOST ||
        window.location.origin
    ).toString();
  } catch {
    return "";
  }
}

function artifactEntry(
  payload,
  format
) {
  const artifacts =
    safeObject(
      safeObject(payload).artifacts
    );

  if (format === "docx") {
    return safeObject(
      artifacts.docx ||
        artifacts.word
    );
  }

  if (format === "text") {
    return safeObject(
      artifacts.text ||
        artifacts.txt
    );
  }

  return safeObject(
    artifacts.pdf
  );
}

function artifactUrl(
  payload,
  format
) {
  const artifact =
    artifactEntry(
      payload,
      format
    );

  const value =
    firstNonEmpty(
      artifact.readUrl,
      artifact.read_url,
      artifact.viewerUrl,
      artifact.viewer_url,
      artifact.url,
      artifact.downloadUrl,
      artifact.download_url
    );

  return isOpenableUrl(value)
    ? absolutizeUrl(value)
    : "";
}

function availableFormatsFromPayload(
  payload
) {
  const envelope =
    safeObject(payload);
  const declared =
    Array.isArray(
      envelope.availableFormats
    )
      ? envelope.availableFormats
      : [];

  const allowed =
    new Set(
      declared
        .map(normalizeFormat)
        .filter(Boolean)
    );

  // A button is rendered only when the endpoint returned a usable signed URL.
  FORMAT_ORDER.forEach(
    (format) => {
      if (
        artifactUrl(
          envelope,
          format
        )
      ) {
        allowed.add(format);
      }
    }
  );

  return FORMAT_ORDER.filter(
    (format) =>
      allowed.has(format) &&
      Boolean(
        artifactUrl(
          envelope,
          format
        )
      )
  );
}

function defaultFormatFromPayload(
  payload,
  availableFormats
) {
  const backendDefault =
    normalizeFormat(
      safeObject(payload)
        .defaultFormat
    );

  if (
    backendDefault &&
    availableFormats.includes(
      backendDefault
    )
  ) {
    return backendDefault;
  }

  if (
    availableFormats.includes("pdf")
  ) {
    return "pdf";
  }

  return availableFormats[0] || "";
}

function extractTitle(
  payload,
  hintedTitle
) {
  const envelope =
    safeObject(payload);

  return (
    safeString(
      firstNonEmpty(
        envelope.title,
        envelope.documentTitle,
        envelope.document_title,
        envelope.displayTitle,
        envelope.display_title,
        envelope.subject,
        hintedTitle
      )
    ) ||
    "Document"
  );
}

function extractFinalizedAt(
  payload
) {
  const envelope =
    safeObject(payload);

  return firstNonEmpty(
    envelope.finalizedAt,
    envelope.finalized_at,
    envelope.completedAt,
    envelope.completed_at,
    envelope.publishedAt,
    envelope.published_at,
    envelope.updatedAt,
    envelope.updated_at,
    envelope.createdAt,
    envelope.created_at
  );
}

/* -------------------------- Authentication helpers ----------------------- */

async function getIdToken() {
  try {
    const user =
      getAuth()?.currentUser;

    if (!user) {
      return "";
    }

    return (
      (await user.getIdToken(
        false
      )) ||
      ""
    );
  } catch {
    return "";
  }
}

async function authJson(
  method,
  url,
  body
) {
  const idToken =
    await getIdToken();

  if (!idToken) {
    throw new Error(
      "Please sign in again to view this finalized document."
    );
  }

  const response =
    await fetch(
      url,
      {
        method,
        headers: {
          Authorization:
            `Bearer ${idToken}`,
          ...(body !== undefined
            ? {
                "Content-Type":
                  "application/json",
              }
            : {}),
        },
        body:
          body !== undefined
            ? JSON.stringify(body)
            : undefined,
        credentials: "include",
      }
    );

  const data =
    await response
      .json()
      .catch(() => ({}));

  if (!response.ok) {
    const error =
      new Error(
        data?.error?.message ||
          data?.error ||
          data?.message ||
          `Request failed (${response.status})`
      );

    error.status =
      response.status;
    error.code =
      data?.code ||
      data?.error?.code ||
      "";
    error.payload = data;

    throw error;
  }

  return data;
}

async function fetchFinalizedArtifacts(
  jobId
) {
  if (!jobId) {
    throw new Error(
      "A Doc Writer job ID is required."
    );
  }

  return authJson(
    "GET",
    `${DOCWRITER_JOBS_BASE}/${encodeURIComponent(
      jobId
    )}/finalized-artifacts`
  );
}

/* --------------------------- Viewer record helpers ----------------------- */

function buildViewerRecord({
  format,
  title,
  finalizedAt,
  source,
  artifact,
  publishedId,
  sessionId,
}) {
  const details =
    FORMAT_DETAILS[format];

  const backendFilename =
    safeString(
      firstNonEmpty(
        artifact.name,
        artifact.filename,
        artifact.fileName
      )
    );

  const filename =
    backendFilename ||
    ensureExtension(
      title,
      details.extension
    );

  const contentType =
    safeString(
      firstNonEmpty(
        artifact.contentType,
        artifact.content_type,
        details.contentType
      )
    );

  const downloadUrl =
    safeString(
      firstNonEmpty(
        artifact.downloadUrl,
        artifact.download_url,
        source
      )
    );

  const common = {
    id:
      publishedId ||
      sessionId,
    nodeId: "",
    kind: "file",
    subject: title,
    title,
    name: filename,
    filename,
    createdAt: finalizedAt,
    finalizedAt,
    contentType,
    format: details.format,
    fileFormat: format,
    sizeBytes:
      artifact.sizeBytes ??
      artifact.size_bytes ??
      null,
    sha256:
      safeString(
        artifact.sha256
      ),
    allowDownload: true,
    linkAllowDownload: true,
    viewerUrl: source,
    url: source,
    downloadUrl,
    urls: {
      pdf: "",
      docx: "",
      text: "",
    },
  };

  if (format === "pdf") {
    return {
      ...common,
      pdfUrl: source,
      pdfUrlInline: source,
      urls: {
        ...common.urls,
        pdf: source,
      },
    };
  }

  if (format === "docx") {
    return {
      ...common,
      format: "office",
      urls: {
        ...common.urls,
        docx: source,
      },
    };
  }

  return {
    ...common,
    format: "text",
    urls: {
      ...common.urls,
      text: source,
    },
  };
}

/* ---------------------------------- Page --------------------------------- */

const DocViewerPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const params = useMemo(
    () =>
      new URLSearchParams(
        location.search
      ),
    [location.search]
  );

  const navigationState =
    safeObject(location.state);

  const jobId =
    safeString(
      firstNonEmpty(
        params.get("jobId"),
        navigationState.jobId
      )
    );

  const sessionId =
    safeString(
      firstNonEmpty(
        params.get("sessionId"),
        navigationState.sessionId
      )
    );

  const revisionId =
    safeString(
      firstNonEmpty(
        params.get("revisionId"),
        navigationState.revisionId
      )
    );

  const queryPublishedId =
    safeString(
      firstNonEmpty(
        params.get("publishedId"),
        navigationState.publishedId
      )
    );

  const hintedTitle =
    safeString(
      firstNonEmpty(
        params.get("title"),
        navigationState.title
      )
    );

  const [loading, setLoading] =
    useState(true);
  const [
    formatLoading,
    setFormatLoading,
  ] = useState(false);
  const [error, setError] =
    useState("");
  const [
    formatError,
    setFormatError,
  ] = useState("");
  const [
    publishedId,
    setPublishedId,
  ] = useState(
    queryPublishedId
  );
  const [title, setTitle] =
    useState(
      hintedTitle ||
        "Document"
    );
  const [
    finalizedAt,
    setFinalizedAt,
  ] = useState("");
  const [
    availableFormats,
    setAvailableFormats,
  ] = useState([]);
  const [
    activeFormat,
    setActiveFormat,
  ] = useState("");
  const [record, setRecord] =
    useState(null);

  const payloadRef =
    useRef({});
  const recordCacheRef =
    useRef(
      new Map()
    );
  const formatRequestRef =
    useRef(0);

  const clearViewerCache =
    useCallback(() => {
      recordCacheRef.current.clear();
    }, []);

  useEffect(() => {
    const previousTitle =
      document.title;

    return () => {
      document.title =
        previousTitle;
    };
  }, []);

  const applyPayload =
    useCallback(
      (nextPayload) => {
        const payload =
          safeObject(
            nextPayload
          );
        const formats =
          availableFormatsFromPayload(
            payload
          );
        const resolvedTitle =
          extractTitle(
            payload,
            hintedTitle
          );
        const resolvedFinalizedAt =
          extractFinalizedAt(
            payload
          );
        const resolvedPublishedId =
          safeString(
            firstNonEmpty(
              payload.publishedId,
              payload.finalizationId,
              queryPublishedId,
              sessionId
            )
          );

        payloadRef.current =
          payload;

        setTitle(
          resolvedTitle
        );
        setFinalizedAt(
          resolvedFinalizedAt
        );
        setPublishedId(
          resolvedPublishedId
        );
        setAvailableFormats(
          formats
        );

        document.title =
          resolvedTitle;

        return {
          formats,
          defaultFormat:
            defaultFormatFromPayload(
              payload,
              formats
            ),
        };
      },
      [
        hintedTitle,
        queryPublishedId,
        sessionId,
      ]
    );

  const loadMetadata =
    useCallback(async () => {
      setLoading(true);
      setError("");
      setFormatError("");
      setRecord(null);
      setActiveFormat("");
      setAvailableFormats([]);
      clearViewerCache();

      try {
        if (!jobId) {
          throw new Error(
            "This Doc History link does not include a job ID."
          );
        }

        const payload =
          await fetchFinalizedArtifacts(
            jobId
          );

        const {
          formats,
          defaultFormat,
        } = applyPayload(
          payload
        );

        if (!formats.length) {
          throw new Error(
            "No finalized document artifact is available."
          );
        }

        setActiveFormat(
          defaultFormat
        );
      } catch (loadError) {
        setError(
          loadError?.message ||
            "Failed to open the finalized document."
        );
      } finally {
        setLoading(false);
      }
    }, [
      jobId,
      applyPayload,
      clearViewerCache,
    ]);

  useEffect(() => {
    loadMetadata();
  }, [loadMetadata]);

  const refreshPayload =
    useCallback(async () => {
      const refreshed =
        await fetchFinalizedArtifacts(
          jobId
        );

      applyPayload(
        refreshed
      );
      clearViewerCache();

      return refreshed;
    }, [
      jobId,
      applyPayload,
      clearViewerCache,
    ]);

  const loadFormat =
    useCallback(
      async (format) => {
        if (
          !format ||
          !jobId
        ) {
          return;
        }

        const cached =
          recordCacheRef.current.get(
            format
          );

        if (cached) {
          setRecord(cached);
          setFormatError("");
          return;
        }

        const requestId =
          formatRequestRef.current + 1;

        formatRequestRef.current =
          requestId;

        setFormatLoading(true);
        setFormatError("");
        setRecord(null);

        try {
          let payload =
            payloadRef.current;
          let source =
            artifactUrl(
              payload,
              format
            );

          if (!source) {
            payload =
              await refreshPayload();

            source =
              artifactUrl(
                payload,
                format
              );
          }

          if (!source) {
            throw new Error(
              format === "docx"
                ? "The finalized Word file is not available."
                : format === "text"
                ? "The finalized TXT file is not available."
                : "The finalized PDF is not available."
            );
          }

          const artifact =
            artifactEntry(
              payload,
              format
            );

          const currentTitle =
            extractTitle(
              payload,
              title
            );
          const currentFinalizedAt =
            extractFinalizedAt(
              payload
            ) ||
            finalizedAt;
          const currentPublishedId =
            safeString(
              firstNonEmpty(
                payload.publishedId,
                payload.finalizationId,
                publishedId,
                queryPublishedId
              )
            );
          const currentSessionId =
            safeString(
              firstNonEmpty(
                payload.sessionId,
                sessionId
              )
            );

          const nextRecord =
            buildViewerRecord({
              format,
              title: currentTitle,
              finalizedAt:
                currentFinalizedAt,
              source,
              artifact,
              publishedId:
                currentPublishedId,
              sessionId:
                currentSessionId,
            });

          recordCacheRef.current.set(
            format,
            nextRecord
          );

          if (
            formatRequestRef.current !==
            requestId
          ) {
            return;
          }

          setRecord(
            nextRecord
          );
        } catch (loadError) {
          if (
            formatRequestRef.current !==
            requestId
          ) {
            return;
          }

          setFormatError(
            loadError?.message ||
              `Failed to open the ${FORMAT_DETAILS[format].label} artifact.`
          );
        } finally {
          if (
            formatRequestRef.current ===
            requestId
          ) {
            setFormatLoading(false);
          }
        }
      },
      [
        jobId,
        title,
        finalizedAt,
        publishedId,
        queryPublishedId,
        sessionId,
        refreshPayload,
      ]
    );

  useEffect(() => {
    if (!activeFormat) {
      setRecord(null);
      return;
    }

    loadFormat(
      activeFormat
    );
  }, [
    activeFormat,
    loadFormat,
  ]);

  const getFreshPdfPreviewUrl =
    useCallback(async () => {
      const refreshed =
        await refreshPayload();

      return (
        artifactUrl(
          refreshed,
          "pdf"
        ) ||
        ""
      );
    }, [refreshPayload]);

  const styles = useMemo(
    () => ({
      page: {
        width: "100%",
        maxWidth: 1120,
        margin: "0 auto",
        padding: "18px 16px 44px",
        background: "#F5F7FA",
        boxSizing: "border-box",
      },
      shell: {
        width: "100%",
        maxWidth: "none",
        margin: "12px 0 0",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        border:
          "1px solid #e5e7eb",
        borderRadius: 16,
        background: "#fff",
        boxShadow:
          "0 10px 30px rgba(0,0,0,0.08)",
      },
      selectorBar: {
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        justifyContent:
          "space-between",
        gap: 12,
        padding: "10px 12px",
        borderBottom:
          "1px solid #e5e7eb",
        background: "#ffffff",
      },
      selectorTitle: {
        minWidth: 0,
        color: "#374151",
        fontSize: 12,
        fontWeight: 800,
        letterSpacing: "0.02em",
        whiteSpace: "nowrap",
      },
      selectorButtons: {
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        justifyContent:
          "flex-end",
        gap: 8,
        flexWrap: "wrap",
      },
      formatButton: {
        minHeight: 36,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 7,
        padding: "8px 14px",
        border:
          "1px solid #d1d5db",
        borderRadius: 999,
        background: "#ffffff",
        color: "#374151",
        cursor: "pointer",
        fontSize: 12,
        fontWeight: 850,
        lineHeight: 1,
        whiteSpace: "nowrap",
        transition:
          "background .15s ease, border-color .15s ease, color .15s ease, box-shadow .15s ease",
      },
      activeFormatButton: {
        borderColor: "#1E2A78",
        background: "#1E2A78",
        color: "#ffffff",
        boxShadow:
          "0 4px 12px rgba(30,42,120,.20)",
      },
      body: {
        minHeight: 0,
        flex: "1 1 auto",
        padding: 16,
        paddingBottom: 24,
        overflow: "hidden",
      },
      spacer: {
        height: 8,
      },
      error: {
        margin: 12,
        padding: 10,
        border:
          "1px solid #fecaca",
        borderRadius: 10,
        background: "#fee2e2",
        color: "#b91c1c",
      },
      formatError: {
        marginBottom: 12,
        padding: 10,
        border:
          "1px solid #fed7aa",
        borderRadius: 10,
        background: "#fff7ed",
        color: "#9a3412",
        fontSize: 13,
        lineHeight: 1.45,
      },
      loading: {
        minHeight: 260,
        display: "grid",
        placeItems: "center",
        padding: 24,
        color: "#6b7280",
        textAlign: "center",
      },
      empty: {
        padding: 24,
        color: "#6b7280",
        fontSize: 14,
        textAlign: "center",
      },
      tip: {
        marginTop: 8,
        marginBottom: 0,
        color: "#6b7280",
        textAlign: "center",
        fontSize: 12,
      },
      embeddedCss: `
        .doc-viewer-format-button:hover:not(:disabled) {
          border-color: #1E2A78 !important;
          color: #1E2A78 !important;
        }

        .doc-viewer-format-button.is-active:hover:not(:disabled) {
          color: #ffffff !important;
        }

        .doc-viewer-format-button:focus-visible {
          outline: 3px solid rgba(30, 42, 120, .18);
          outline-offset: 2px;
        }

        .doc-viewer-format-button:disabled {
          cursor: not-allowed !important;
          opacity: .58;
        }

        .viewer-embed {
          min-width: 0;
          min-height: 0;
        }

        .viewer-embed [role="dialog"][aria-label="Document Viewer"] {
          position: static !important;
          inset: auto !important;
          z-index: auto !important;
          padding: 0 !important;
          background: transparent !important;
        }

        .viewer-embed [role="dialog"][aria-label="Document Viewer"] > div {
          position: relative !important;
          left: 0 !important;
          top: 0 !important;
          width: 100% !important;
          height: 78vh !important;
          max-width: none !important;
          max-height: none !important;
          border-radius: 12px !important;
          box-shadow: none !important;
        }

        /*
         * Contact-page responsive rule:
         * <= 1024px = mobile/tablet
         * > 1024px = laptop/desktop
         *
         * The outer width, left/right edge, and top starting point intentionally
         * match Contact.jsx one-to-one:
         * - desktop: 1120px max width, 16px sides, viewer starts at 30px
         * - mobile: 12px sides, viewer starts at 26px
         */
        @media (max-width: 1024px) {
          .doc-viewer-page {
            width: 100% !important;
            max-width: 1120px !important;
            margin: 0 auto !important;
            padding: 14px 12px 34px !important;
            background: #F5F7FA !important;
            box-sizing: border-box !important;
          }

          .doc-viewer-shell {
            width: 100% !important;
            max-width: none !important;
            min-height: 0 !important;
            height: auto !important;
            max-height: none !important;
            margin: 12px 0 0 !important;
          }

          .doc-viewer-selector {
            min-height: 50px !important;
            flex: 0 0 auto !important;
            justify-content: space-between !important;
            padding: 8px 10px !important;
            gap: 10px !important;
          }

          .doc-viewer-selector-title {
            display: block !important;
            flex: 0 0 auto !important;
            min-width: max-content !important;
            white-space: nowrap !important;
          }

          .doc-viewer-selector-buttons {
            width: auto !important;
            min-width: 0 !important;
            flex: 1 1 auto !important;
            justify-content: flex-end !important;
            gap: 6px !important;
            flex-wrap: nowrap !important;
            overflow-x: auto !important;
            overflow-y: hidden !important;
            scroll-behavior: smooth;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
          }

          .doc-viewer-selector-buttons::-webkit-scrollbar {
            display: none;
          }

          .doc-viewer-format-button {
            min-height: 34px !important;
            padding: 7px 13px !important;
            flex: 0 0 auto !important;
          }

          .doc-viewer-body {
            min-width: 0 !important;
            min-height: 0 !important;
            height: auto !important;
            flex: 1 1 auto !important;
            padding: 12px 12px 14px !important;
            overflow: hidden !important;
            background: #ffffff !important;
          }

          .viewer-embed {
            width: 100% !important;
            height: auto !important;
            min-height: 0 !important;
          }

          .viewer-embed [role="dialog"][aria-label="Document Viewer"] {
            width: 100% !important;
            height: auto !important;
            overflow: visible !important;
          }

          .viewer-embed [role="dialog"][aria-label="Document Viewer"] > div {
            width: 100% !important;
            height: min(68dvh, 700px) !important;
            max-height: calc(100dvh - 150px) !important;
            min-height: 420px !important;
            border: 1px solid #e5e7eb !important;
            border-radius: 12px !important;
            overflow: hidden !important;
          }

          .doc-viewer-tip {
            display: block !important;
            margin: 10px 4px 0 !important;
            padding: 0 4px !important;
          }

          .doc-viewer-spacer {
            display: block !important;
            height: 8px !important;
          }

          .doc-viewer-format-error {
            margin: 8px !important;
          }
        }
      `,
    }),
    []
  );

  return (
    <div
      className="doc-viewer-page"
      style={styles.page}
    >
      <style>
        {styles.embeddedCss}
      </style>

      <div
        className="doc-viewer-shell"
        style={styles.shell}
      >
        {!loading &&
        !error &&
        availableFormats.length ? (
          <div
            className="doc-viewer-selector"
            style={styles.selectorBar}
            aria-label="Finalized document formats"
          >
            <div
              className="doc-viewer-selector-title"
              style={styles.selectorTitle}
            >
              Available files
            </div>

            <div
              className="doc-viewer-selector-buttons"
              style={styles.selectorButtons}
            >
              {availableFormats.map(
                (format) => {
                  const active =
                    activeFormat ===
                    format;

                  return (
                    <button
                      key={format}
                      type="button"
                      className={`doc-viewer-format-button ${
                        active
                          ? "is-active"
                          : ""
                      }`}
                      style={{
                        ...styles.formatButton,
                        ...(active
                          ? styles.activeFormatButton
                          : {}),
                      }}
                      disabled={
                        formatLoading &&
                        active
                      }
                      onClick={() => {
                        if (
                          format !==
                          activeFormat
                        ) {
                          setActiveFormat(
                            format
                          );
                        }
                      }}
                      aria-pressed={active}
                    >
                      {
                        FORMAT_DETAILS[
                          format
                        ].label
                      }
                    </button>
                  );
                }
              )}
            </div>
          </div>
        ) : null}

        {error ? (
          <div
            role="alert"
            style={styles.error}
          >
            {error}
          </div>
        ) : null}

        <div
          className="doc-viewer-body"
          style={styles.body}
        >
          {loading ? (
            <div
              className="screen-only"
              style={styles.loading}
            >
              Loading finalized document…
            </div>
          ) : formatError ? (
            <>
              <div
                className="doc-viewer-format-error"
                role="alert"
                style={styles.formatError}
              >
                {formatError}
              </div>

              <div style={styles.empty}>
                Select another available file format above.
              </div>
            </>
          ) : formatLoading ? (
            <div
              className="screen-only"
              style={styles.loading}
            >
              Opening{" "}
              {FORMAT_DETAILS[
                activeFormat
              ]?.label || "document"}
              …
            </div>
          ) : record ? (
            <div className="viewer-embed">
              <FileViewerModal
                key={`${publishedId}:${activeFormat}`}
                open={true}
                onClose={() =>
                  navigate(-1)
                }
                record={{
                  ...record,
                  jobId,
                  sessionId,
                  revisionId,
                  publishedId,
                }}
                getBrowserFallbackUrl={
                  activeFormat === "pdf"
                    ? getFreshPdfPreviewUrl
                    : undefined
                }
              />
            </div>
          ) : !error ? (
            <div style={styles.empty}>
              No finalized document artifact is available.
            </div>
          ) : null}

          {!loading &&
          !error &&
          record ? (
            <div
              className="screen-only doc-viewer-tip"
              style={styles.tip}
            >
              PDF tools are available inside the PDF Viewer. Word and TXT use
              the File Viewer controls.
            </div>
          ) : null}

          <div
            className="doc-viewer-spacer"
            style={styles.spacer}
          />
        </div>
      </div>
    </div>
  );
};

export default DocViewerPage;
